import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useContext, useEffect, useState } from "react";
import { buildAdminUserNavigationState, buildDepartmentDetailPath, buildDepartmentUserDetailPath, PATH_INFO, shouldStopForInvalidContextFetchResult } from "../../../util/helper";
import { Badge, Button, Col, Divider, Flex, List, Row, Space, Spin, Table } from "antd";
import { EditOutlined, LeftOutlined, ProfileOutlined, UserAddOutlined } from "@ant-design/icons";
import Column from "antd/es/table/Column";
import type { TablePaginationConfig } from "antd/es/table";
import { UpdateUsersContext, UserResponseType } from "../../../../context/userContext";
import { DepartmentContext, UpdateDepartmentContext } from "../../../../context/departmentContext";
import { UpdateCompanyContext } from "../../../../context/companyContext";
import { notifyApiError } from "../../../../utils/apiErrorHandler";
import { useTranslation } from "react-i18next";

const USER_PAGE_SIZE = 5;

export function DepartmentDetail() {
  const { t } = useTranslation();
  const { refreshDepartmentUsers, toggleUserDisabled } = useContext(UpdateUsersContext);
  const { departmentDetail } = useContext(DepartmentContext);
  const { refreshDepartmentDetail } = useContext(UpdateDepartmentContext);
  const { refreshCompany } = useContext(UpdateCompanyContext);
  const { companyId, departmentId } = useParams();
  const navigate = useNavigate();

  const [departmentUsers, setDepartmentUsers] = useState<UserResponseType[]>([]);
  const [userPagination, setUserPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: USER_PAGE_SIZE,
    total: 0,
  });
  const [activeUserCount, setActiveUserCount] = useState<number>(0);
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(true);
  const [currentAntdSortField, setCurrentAntdSortField] = useState<string | string[] | undefined>(undefined);
  const [currentAntdSortOrder, setCurrentAntdSortOrder] = useState<'ascend' | 'descend' | undefined>(undefined);
  const [isPageReady, setIsPageReady] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    const validateAndLoadDepartmentDetail = async () => {
      setIsPageReady(false);

      if (!companyId || !departmentId) {
        navigate(PATH_INFO.ERROR.PATH, { replace: true });
        return;
      }

      try {
        const [companyResult, departmentResult] = await Promise.all([
          refreshCompany(companyId),
          refreshDepartmentDetail(departmentId),
        ]);

        if (isCancelled) {
          return;
        }

        if (
          shouldStopForInvalidContextFetchResult(companyResult, navigate) ||
          shouldStopForInvalidContextFetchResult(departmentResult, navigate)
        ) {
          return;
        }

        const departmentData = departmentResult.data;
        if (!departmentData) {
          navigate(PATH_INFO.ERROR.PATH, { replace: true });
          return;
        }

        if (departmentData.company?.id !== Number(companyId)) {
          navigate(PATH_INFO.ERROR.PATH, { replace: true });
          return;
        }

        setIsPageReady(true);
      } catch (error) {
        console.error("Error validating department detail.", error);
        if (isCancelled) {
          return;
        }
        navigate(PATH_INFO.ERROR.PATH, { replace: true });
      }
    };

    validateAndLoadDepartmentDetail();

    return () => {
      isCancelled = true;
    };
  }, [companyId, departmentId, navigate, refreshCompany, refreshDepartmentDetail]);

  const fetchDepartmentUsers = useCallback(async (page?: number, pageSize?: number, sortField?: string, sortOrder?: string) => {
    if (!departmentId || !isPageReady) {
      setDepartmentUsers([]);
      setUserPagination({
        current: 1,
        pageSize: pageSize ?? USER_PAGE_SIZE,
        total: 0,
      });
      setActiveUserCount(0);
      setIsLoadingUsers(false);
      return;
    }

    setIsLoadingUsers(true);

    const targetPage = page ?? 1;
    const targetPageSize = pageSize ?? USER_PAGE_SIZE;

    try {
      const { data, meta } = await refreshDepartmentUsers(departmentId, targetPage, targetPageSize, sortField, sortOrder);

      setDepartmentUsers(data);
      setUserPagination({
        current: meta.page ?? targetPage,
        pageSize: meta.limit ?? targetPageSize,
        total: meta.total ?? data.length,
      });
      setActiveUserCount(meta.active ?? data.filter((user) => user.is_disabled === false).length);
    } catch (error) {
      notifyApiError({ error, logMessage: "Error fetching department users" });
      setDepartmentUsers([]);
      setUserPagination({
        current: 1,
        pageSize: targetPageSize,
        total: 0,
      });
      setActiveUserCount(0);
    } finally {
      setIsLoadingUsers(false);
    }
  }, [departmentId, isPageReady, refreshDepartmentUsers]);

  useEffect(() => {
    setDepartmentUsers([]);
    setUserPagination({ current: 1, pageSize: USER_PAGE_SIZE, total: 0 });
    setCurrentAntdSortField(undefined);
    setCurrentAntdSortOrder(undefined);
    setActiveUserCount(0);
    if (!isPageReady) {
      setIsLoadingUsers(false);
      return;
    }
    fetchDepartmentUsers(1, USER_PAGE_SIZE);
  }, [fetchDepartmentUsers, isPageReady]);

  const handleUserTableChange = useCallback((pagination: TablePaginationConfig, _filters: any, sorter: any) => {
    const nextPage = pagination.current ?? 1;
    const nextPageSize = pagination.pageSize ?? USER_PAGE_SIZE;

    const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    let backendSortField: string | undefined;
    let backendSortOrder: string | undefined;
    let antdSortField: string | string[] | undefined;
    let antdSortOrder: 'ascend' | 'descend' | undefined;

    if (currentSorter && currentSorter.field && currentSorter.order) {
      antdSortField = currentSorter.field as string | string[];
      antdSortOrder = currentSorter.order;

      if (antdSortField === 'name') {
        backendSortField = 'username';
      } else {
        backendSortField = antdSortField as string;
      }

      if (antdSortOrder === 'ascend') {
        backendSortOrder = 'ASC';
      } else if (antdSortOrder === 'descend') {
        backendSortOrder = 'DESC';
      }
    }
    setCurrentAntdSortField(antdSortField);
    setCurrentAntdSortOrder(antdSortOrder);
    fetchDepartmentUsers(nextPage, nextPageSize, backendSortField, backendSortOrder);
  }, [fetchDepartmentUsers]);

  const onClickCompanyManage = useCallback(() => {
    if (!companyId) return;
    const result = PATH_INFO.COMPANY_MANAGE.PATH.replace(":companyId", companyId);

    navigate(result);
  }, [companyId, navigate]);

  const onClickUserTile = useCallback((id?: string) => {
    if (!companyId || !departmentId || !id) return;
    const destination = buildDepartmentUserDetailPath(companyId, departmentId, id);
    const backTo = buildDepartmentDetailPath(companyId, departmentId);
    if (!destination || !backTo) return;
    navigate(destination, { state: buildAdminUserNavigationState(backTo, undefined, true) });
  }, [companyId, departmentId, navigate]);

  const onClickUserRegister = useCallback(() => {
    if (!companyId || !departmentId) return;
    const result = PATH_INFO.DEPARTMENT_USER_MANAGE.PATH.replace(":companyId", companyId);
    const _result = result.replace(":departmentId", departmentId);
    const __result = _result.replace(":userId", "register");
    const backTo = buildDepartmentDetailPath(companyId, departmentId);
    navigate(__result, { state: buildAdminUserNavigationState(backTo, backTo, true, true) });
  }, [companyId, departmentId, navigate]);

  const onClickManage = useCallback((id?: string) => {
    if (!companyId || !departmentId || !id) return;
    const result = PATH_INFO.DEPARTMENT_USER_MANAGE.PATH.replace(":companyId", companyId);
    const _result = result.replace(":departmentId", departmentId);
    const __result = _result.replace(":userId", id);
    const backTo = buildDepartmentDetailPath(companyId, departmentId);
    navigate(__result, { state: buildAdminUserNavigationState(backTo, backTo, true) });
  }, [companyId, departmentId, navigate]);

  const onClickToggleStatus = useCallback(async (userId?: string) => {
    if (!userId) return;

    try {
      await toggleUserDisabled(userId);
    } finally {
      await fetchDepartmentUsers(userPagination.current ?? 1, userPagination.pageSize ?? USER_PAGE_SIZE);
    }
  }, [toggleUserDisabled, fetchDepartmentUsers, userPagination]);

  const getOnUserCell = (record: UserResponseType) => ({
    onClick: () => onClickUserTile(record.id),
    style: {
      background: record.is_disabled ? "rgba(0, 0, 0, 0.04)" : "",
      color: record.is_disabled ? "rgba(0,0,0,0.5)" : "",
      cursor: "pointer",
    },
  });

  const departmentInfoTitle = [
    { key: "brandName", label: t("company.brandName"), value: departmentDetail.brand_name },
    { key: "name", label: t("company.departmentName"), value: departmentDetail.name },
    { key: "memo", label: t("company.departmentMemo"), value: departmentDetail.memo || "" },
  ];

  if (!isPageReady) {
    return (
      <Flex justify="center" align="center" className="min-h-[50vh]">
        <Spin size="large" tip={t("common.loadingWithDots")} />
      </Flex>
    );
  }

  return (
    <div className="mb-20">
      <Button variant="text" color="primary" icon={<LeftOutlined />} onClick={() => navigate(-1)} className="mb-2 -ml-3">
        {t("common.back")}
      </Button>
      <div className="flex w-full space-x-5 items-center">
        <p className="flex text-2xl decoration-solid ml-2 mt-0 mb-0 border-0 border-l-4 border-solid border-[#1677ff] p-2">{t("company.departmentDetailPage")}</p>
        <Button variant="solid" color="primary" icon={<EditOutlined />} onClick={onClickCompanyManage}>
          {t("company.editCompanyDepartment")}
        </Button>
      </div>
      <Divider />
      <div className="flex p-2 text-xl mb-3">
        <ProfileOutlined className="text-[#1677ff]" />
        <span className="pl-2 break-all">{`${departmentDetail.company.company_name} / ${departmentDetail.name}`}</span>
      </div>
      <Row justify={"center"} align={"top"} gutter={[24, 24]}>
        {departmentInfoTitle.map((section, index) => (
          <Col xs={12} xl={6} key={index}>
            {section.key === "memo" ? (
              <List
                header={<div className="font-bold">{section.label}</div>}
                bordered
                dataSource={[section.value]}
                renderItem={(item) => (
                  <div className="overflow-y-auto max-h-[100px]">
                    <List.Item style={{ background: "#FFFFFF" }}>
                      <p className="m-0 pl-2 whitespace-pre-wrap  min-h-[22px]">{item}</p>
                    </List.Item>
                  </div>
                )}
                className="bg-[#FAFAFA] overflow-hidden"
              />
            ) : (
              <List
                header={<div className="font-bold">{section.label}</div>}
                bordered
                dataSource={[section.value]}
                renderItem={(item) => (
                  <List.Item style={{ background: "#FFFFFF" }}>
                    <p className="m-0 pl-2">{item}</p>
                  </List.Item>
                )}
                className="overflow-hidden bg-[#FAFAFA]"
              />
            )}
          </Col>
        ))}
      </Row>
      <Divider />
      <div className="flex p-2 text-xl mb-3">
        <ProfileOutlined className="text-[#1677ff]" />
        <span className="pl-2">{t("company.userList")}</span>
      </div>
      <div className="mb-1 pr-5 flex justify-end w-full">
        <Badge status="success" />
        <span className="pl-1">{t("company.activeUserCount", { active: activeUserCount, total: userPagination.total ?? departmentUsers.length })}</span>
      </div>
      <Flex vertical justify="center" className="relative">
        <Table<UserResponseType>
          dataSource={departmentUsers}
          bordered
          loading={isLoadingUsers}
          pagination={{
            current: userPagination.current ?? 1,
            pageSize: userPagination.pageSize ?? USER_PAGE_SIZE,
            total: userPagination.total ?? departmentUsers.length,
            position: ["bottomCenter"],
          }}
          onChange={handleUserTableChange}
          rowKey={(record) => record.id ?? ""}
          scroll={{ x: 'max-content' }}
        >
          <Column
            title={t("company.position")}
            dataIndex="position"
            key="position"
            onCell={getOnUserCell}
            sorter={true}
            sortOrder={currentAntdSortField === 'position' ? currentAntdSortOrder : undefined}
          />
          <Column
            title={t("company.name")}
            dataIndex="name"
            key="name"
            onCell={getOnUserCell}
            sorter={true}
            sortOrder={currentAntdSortField === 'name' ? currentAntdSortOrder : undefined}
          />
          <Column title={t("auth.email")} dataIndex="email" key="email" onCell={getOnUserCell} />
          <Column title="PIN" dataIndex="pin_code" key="pin_code" onCell={getOnUserCell} />
          <Column
            title={t("common.operation")}
            dataIndex="action"
            key="action"
            fixed="right"
            render={(_: any, record: UserResponseType) => (
              <Space size="middle">
                <Button variant="outlined" color="primary" onClick={() => onClickManage(record.id)}>
                  {t("common.edit")}
                </Button>
                <Button variant="outlined" color={record.is_disabled ? "green" : "danger"} onClick={() => onClickToggleStatus(record.id)}>
                  {record.is_disabled ? t("common.restore") : t("common.stop")}
                </Button>
              </Space>
            )}
            width="145px"
          />
        </Table>
      </Flex>
      <Flex
        gap="small"
        className={userPagination.total === 0 && !isLoadingUsers ? "mt-5 justify-end" : "justify-end"}
      >
        <Button type="primary" icon={<UserAddOutlined />} onClick={onClickUserRegister}>
          {t("company.userRegister")}
        </Button>
      </Flex>
    </div>
  );
}
